import { AlertCircle, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ReauthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: (reauthToken: string) => void;
}

export function ReauthDialog({ open, onOpenChange, onVerified }: ReauthDialogProps) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPassword("");
      setError(null);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch("/api/auth/verify-reauth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (res.status === 423) {
        const data: { lockedUntil?: string } = await res.json();
        const until = data.lockedUntil
          ? new Date(data.lockedUntil).toLocaleTimeString()
          : "a few minutes";
        setError(`Account locked until ${until} due to too many failed attempts.`);
        return;
      }

      if (!res.ok) {
        const data: { attemptsRemaining?: number } = await res.json();
        const remaining = data.attemptsRemaining;
        setError(
          remaining !== undefined
            ? `Incorrect password. ${remaining} attempt${remaining === 1 ? "" : "s"} remaining.`
            : "Incorrect password.",
        );
        return;
      }

      const data: { reauthToken: string } = await res.json();
      onVerified(data.reauthToken);
      onOpenChange(false);
    } catch {
      setError("Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-amber-500/10 p-2 ring-1 ring-amber-500/20">
              <ShieldAlert className="size-4 text-amber-500" />
            </div>
            <DialogTitle>Confirm your identity</DialogTitle>
          </div>
          <DialogDescription>This action requires your password to continue.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-500/10 p-3 text-sm text-red-600 ring-1 ring-red-500/20 dark:text-red-400">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reauth-password">Password</Label>
            <Input
              id="reauth-password"
              type="password"
              autoFocus
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !password}>
              {loading ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <KeyRound className="mr-2 size-4" />
              )}
              Verify
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
